/**
 * The in-product guide for standing up a Teams destination.
 *
 * WHY THIS EXISTS AS A FULL GUIDE AND NOT A TOOLTIP
 * -------------------------------------------------
 * Nothing on this screen can create the Teams destination for the
 * customer: the workflow is created inside THEIR Microsoft tenant, by a
 * person who owns the channel. Plan C goal #6 says that when a step
 * cannot happen in our web, the UI must explain it precisely — the exact
 * menu path in their system, and what to bring back here — rather than
 * shipping a button that lies or a link dump.
 *
 * It also carries the bad news: classic Office 365 connector webhooks
 * (`*.webhook.office.com`) were switched off by Microsoft in the 18-22
 * May 2026 window. A customer who already had "the Teams webhook URL"
 * has a dead one, and the only visible symptom would be deliveries that
 * stop arriving. Saying so here is cheaper than a support ticket.
 *
 * Every string is an i18n key present in EN and ES.
 */

import { AlertTriangle, ExternalLink, Info } from "lucide-react";

import { useT } from "@/i18n";
import { envVarNameFor, isSecretRefValid } from "@/pages/delivery/delivery-form";

export interface TeamsWorkflowGuideProps {
  /** The reference typed in the form, used to name the exact env var. */
  secretRef: string;
  /**
   * True once the Workflow URL is stored through the web (Plan C 2.2).
   *
   * The final step used to be "set this environment variable on the
   * builder container", because that was the only place a credential
   * could live. With the writable secret backend it is "paste it in the
   * field above", and showing the env-var instruction to someone who
   * just typed the URL into a form would be telling them to do a second,
   * unnecessary thing — and implying the first one did not take.
   *
   * Defaults to false so the Delivery panel, which still points at env
   * vars for its legacy refs, keeps saying exactly what it said before.
   */
  storedInWeb?: boolean;
}

const STEP_KEYS = [
  "delivery.teams.guide.step1",
  "delivery.teams.guide.step2",
  "delivery.teams.guide.step3",
  "delivery.teams.guide.step4",
  "delivery.teams.guide.step5",
  "delivery.teams.guide.step6",
] as const;

const WARNING_KEYS = [
  "delivery.teams.guide.warnOwner",
  "delivery.teams.guide.warnLimits",
] as const;

const UNAVAILABLE_KEYS = [
  "delivery.teams.guide.noBotIdentity",
  "delivery.teams.guide.noInteractive",
] as const;

export function TeamsWorkflowGuide({
  secretRef,
  storedInWeb = false,
}: TeamsWorkflowGuideProps): JSX.Element {
  const { t } = useT();
  // Show the real variable once the reference is well formed; before
  // that, show the shape rather than a half-built name that would be
  // wrong if pasted.
  const envVar = isSecretRefValid(secretRef)
    ? envVarNameFor(secretRef)
    : envVarNameFor("tenants/<tenant>/delivery/teams/<name>");

  return (
    <section className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm">
      <header className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-neutral-900">
          {t("delivery.teams.guide.title")}
        </h3>
        <span className="rounded-full border border-neutral-300 bg-white px-2 py-0.5 text-xs text-neutral-600">
          {t("delivery.teams.guide.doneInTeams")}
        </span>
      </header>

      <p
        role="note"
        className="mt-3 flex items-start gap-2 rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-neutral-800"
      >
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>{t("delivery.teams.guide.retiredNotice")}</span>
      </p>

      <p className="mt-3 text-neutral-700">
        {t("delivery.teams.guide.intro")}
      </p>

      <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-neutral-700">
        {STEP_KEYS.map((key) => (
          <li key={key}>{t(key)}</li>
        ))}
        <li>
          {storedInWeb
            ? t("delivery.teams.guide.step7Web")
            : t("delivery.teams.guide.step7", { envVar })}
        </li>
      </ol>

      <p className="mt-3 flex items-start gap-2 text-xs text-neutral-600">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>{t("delivery.teams.guide.refNote")}</span>
      </p>

      <h4 className="mt-4 font-medium text-neutral-800">
        {t("delivery.teams.guide.warnTitle")}
      </h4>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-neutral-700">
        {WARNING_KEYS.map((key) => (
          <li key={key}>{t(key)}</li>
        ))}
      </ul>

      <h4 className="mt-4 font-medium text-neutral-800">
        {t("delivery.teams.guide.unavailableTitle")}
      </h4>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-neutral-700">
        {UNAVAILABLE_KEYS.map((key) => (
          <li key={key}>{t(key)}</li>
        ))}
      </ul>

      {/* The ONE Microsoft URL verified for this change: the retirement
          announcement. No deep-links into support articles were added,
          because an unverified doc URL that 404s is worse than none. */}
      <a
        className="mt-4 inline-flex items-center gap-1 text-xs text-accent underline"
        href="https://devblogs.microsoft.com/microsoft365dev/retirement-of-office-365-connectors-within-microsoft-teams/"
        target="_blank"
        rel="noreferrer noopener"
      >
        {t("delivery.teams.guide.docsLink")}
        <ExternalLink size={12} />
      </a>
    </section>
  );
}

export default TeamsWorkflowGuide;
